import { NSButton, NSCard, NSCheckbox } from 'bricks';
import React, { useMemo } from 'react';
import { useGetAttributes } from 'components/DealForm/hooks/useGetAttributes';
import { DEAL_OVERVIEW_ATTRIBUTES, DEAL_OVERVIEW_ATTRIBUTES_TO_DEAL_PROPS } from 'views/ReportBuilder/constants';
import { formatDealHeader, buildDealOverviewAttributes } from 'views/ReportBuilder/helpers';
import { DealOverviewAttributes } from 'views/ReportBuilder/types';

export interface IProps {
    checkedAttributeState: Record<string, { checked: boolean; correlationId: string }>;
    setCheckedAttributeState: React.Dispatch<React.SetStateAction<Record<string, { checked: boolean; correlationId: string }>>>;
}

const AttributesSection = ({ checkedAttributeState, setCheckedAttributeState }: IProps) => {
    const { data: dealAttributes } = useGetAttributes();
    const handleCheckboxChange = (name: string, correlationId: string) => {
        setCheckedAttributeState(prev => ({
            ...prev,
            [name]: {
                checked: !(prev[name]?.checked ?? false),
                correlationId,
            },
        }));
    };

    const dealOverviewAttributes = useMemo(
        () => {
            if (!dealAttributes) {
                return DEAL_OVERVIEW_ATTRIBUTES;
            }
            return buildDealOverviewAttributes(dealAttributes);
        }, [dealAttributes],
    );

    const handleSelectDeselectAll = (
        group: keyof DealOverviewAttributes,
        selectAll: boolean,
    ) => {
        const updatedItems = ((dealOverviewAttributes ?? {}) as DealOverviewAttributes)[group]?.reduce((acc, item) => {
            const propName = DEAL_OVERVIEW_ATTRIBUTES_TO_DEAL_PROPS[item.name] || item.name;
            acc[propName] = {
                checked: selectAll,
                correlationId: item.correlationId,
            };
            return acc;
        }, {} as Record<string, { checked: boolean; correlationId: string }>);

        setCheckedAttributeState(prev => ({
            ...prev,
            ...updatedItems,
        }));
    };
    return (
        <div>
            {Object.entries(dealOverviewAttributes).map(([header, items]: [string, { name: string; correlationId: string }[]]) => {
                const allSelected = items.every(item => {
                    const propName = DEAL_OVERVIEW_ATTRIBUTES_TO_DEAL_PROPS[item.name] || item.name;
                    return checkedAttributeState[propName]?.checked;
                });

                return (
                    <NSCard key={header} className="NSCard--level-3 p-3 mb-1">
                        <div className="d-flex justify-content-between align-items-center m-0">
                            <h5 className="mt-0 mb-2">{formatDealHeader(header)}</h5>
                            <NSButton
                                className="p-0 text-primary"
                                callback={() => handleSelectDeselectAll(header as keyof DealOverviewAttributes, !allSelected)}
                            >
                                {allSelected ? 'Deselect all' : 'Select all'}
                            </NSButton>
                        </div>
                        <div className="d-flex flex-column">
                            {items.map(item => {
                                const propName = DEAL_OVERVIEW_ATTRIBUTES_TO_DEAL_PROPS[item.name] || item.name;

                                return (
                                    <NSCheckbox
                                        key={item.name}
                                        id={item.name}
                                        labelText={item.name}
                                        checked={checkedAttributeState[propName]?.checked || false}
                                        callback={() => handleCheckboxChange(propName, item.correlationId)}
                                    />
                                );
                            })}
                        </div>
                    </NSCard>
                );
            })}
        </div>
    );
};

export default AttributesSection;
